import { useTranslation } from "react-i18next";
import { Icon, Label } from "semantic-ui-react";

import { CUSTOMER_TYPES, DESK_COMMUNICATION_EVENTS, KIOSK_COMMUNICATION_EVENT_TYPES } from "models";
import "modules/AppointmentDetails/components/CarDetails/components/CustomerInfo/CustomerInfo.scss";
import { useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import "modules/HoldingAppointmentDetails/components/CarDetails/components/CustomerInfo/CustomerInfo.scss";
import { isAppointmentWarrantyPinSignatureMissing } from "utils/appointmentUtils";
import { getCustomerName } from "utils/common";

import type { TFunction } from "i18next";
import type { Appointment, Brand, DealerLocation } from "models";

type CustomerInfoProps = {
  appointment: Appointment;
  customerType: CUSTOMER_TYPES;
};

const getCustomerTypeToHighlight = (appointment: Appointment) => {
  if (appointment.customer_driver) return CUSTOMER_TYPES.Driver;
  if (appointment.customer_contract) return CUSTOMER_TYPES.Contractor;
  if (appointment.customer_owner) return CUSTOMER_TYPES.Owner;
};

const getCustomerSignatureIndicator = (appointment: Appointment, location: DealerLocation, brands: Brand[], customerType: CUSTOMER_TYPES) => {
  const isWarrantyPinSigantureMissing = isAppointmentWarrantyPinSignatureMissing(appointment, brands, location);

  if (customerType !== getCustomerTypeToHighlight(appointment)) return "";
  if (isWarrantyPinSigantureMissing) return "warranty-signature-missing";
  if (appointment.check_in_com_answered) return "customer-checked-in";
};

const shouldDisplaySignatureLabel = (appointment: Appointment, customerType: CUSTOMER_TYPES) => {
  if (!appointment.check_in_com_answered || customerType !== getCustomerTypeToHighlight(appointment)) return false;

  const hasDeskAnsweredEvent = appointment.desk_communications?.some(dc => dc.events?.some(event => event.type === DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered));
  const hasKioskAnsweredEvent = appointment.kiosk_communications?.some(kc => kc.events?.some(event => event.type === KIOSK_COMMUNICATION_EVENT_TYPES.CheckInAnswered));

  return hasDeskAnsweredEvent || hasKioskAnsweredEvent;
};

const getCustomerDetails = (appointment: Appointment, customerType: CUSTOMER_TYPES, t: TFunction) => {
  if (customerType === CUSTOMER_TYPES.Driver) {
    return {
      title: t("v8_driver", "Driver"),
      customerId: appointment.customer_driver_id,
      customer: appointment.customer_driver,
      customerOptedOutFromImporting: appointment.driver_opted_out_from_importing
    };
  }

  if (customerType === CUSTOMER_TYPES.Owner) {
    return {
      title: t("v8_owner", "Owner"),
      customerId: appointment.customer_owner_id,
      customer: appointment.customer_owner,
      customerOptedOutFromImporting: appointment.owner_opted_out_from_importing
    };
  }

  if (customerType === CUSTOMER_TYPES.Contractor) {
    return {
      title: t("v8_contractor", "Contractor"),
      customerId: appointment.customer_contract_id,
      customer: appointment.customer_contract,
      customerOptedOutFromImporting: appointment.contractor_opted_out_from_importing
    };
  }
};

export const CustomerInfo = ({ appointment, customerType }: CustomerInfoProps) => {
  const { t } = useTranslation();

  const location = useAppointmentLocation();
  const { data: brands = [] } = useCarBrandsQuery();

  const customerDetails = getCustomerDetails(appointment, customerType, t);
  if (!location || !customerDetails) return null;

  const { customerId, title, customer, customerOptedOutFromImporting } = customerDetails;
  const customerName = getCustomerName({ appointment, customerType, showTitle: true, t });

  if (!customerOptedOutFromImporting && (!customerId || !customerName || !customer)) return null;

  return (
    <div className="HoldingCustomerInfo">
      {title}

      {shouldDisplaySignatureLabel(appointment, customerType) && (
        <Label basic className="CustomerInfo">
          <Icon className="signature" color="blue" />
        </Label>
      )}

      <div className={`${getCustomerSignatureIndicator(appointment, location, brands, customerType) ?? ""} holding-customer-name`}>{customerName}</div>
    </div>
  );
};
